import { useCallback, useEffect, useRef, useState } from 'react';
import { BookEngineNav } from '../components/editor/BookEngineNav';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SendIcon from '@mui/icons-material/Send';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  useStoryStudioSession,
  useSuggestBookTitles,
  useUpsertStoryStudio,
  type TitleSuggestionDto,
} from '../api/queries/storyStudio';
import { useChapters } from '../api/queries/books';
import { useMarkLastEngine } from '../api/queries/bookSession';
import { FilterableSelect } from '../components/common/FilterableSelect';
import { useAIAction } from '../hooks/useAIAction';
import { useGenerationStatus } from '../hooks/useGenerationStatus';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  mode: string;
  timestamp: Date;
  accepted?: boolean;
  editing?: boolean;
}

type ChatMode = 'write' | 'expand' | 'rewrite' | 'continue' | 'summarize';

const CHAT_MODES: { value: ChatMode; label: string; description: string }[] = [
  {
    value: 'write',
    label: 'Write',
    description: 'Free-form instruction to AI',
  },
  {
    value: 'expand',
    label: 'Expand',
    description: 'AI expands on the current draft',
  },
  {
    value: 'rewrite',
    label: 'Rewrite',
    description: 'AI rewrites the current draft in a specified style',
  },
  {
    value: 'continue',
    label: 'Continue',
    description: 'AI continues from where the draft ends',
  },
  {
    value: 'summarize',
    label: 'Summarize',
    description: 'AI creates a synopsis of the current draft',
  },
];

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

/**
 * Story Studio session page — two-panel layout with a conversational AI chat
 * interface (left) and a working draft panel (right). Session context fields
 * (synopsis/mood/pace/voice) feed into the AI system prompt.
 */
export function StoryStudioRoute() {
  const { bookId } = useParams({ strict: false }) as { bookId: string };
  const navigate = useNavigate();
  useMarkLastEngine(bookId, 'story-studio');
  const session = useStoryStudioSession(bookId);
  const upsert = useUpsertStoryStudio(bookId);
  const suggestTitles = useSuggestBookTitles(bookId);
  const textAction = useAIAction('text-generation');
  const chapters = useChapters(bookId);

  // Session context fields
  const [synopsis, setSynopsis] = useState('');
  const [worldNotes, setWorldNotes] = useState('');
  const [mood, setMood] = useState('');
  const [pace, setPace] = useState('');
  const [voice, setVoice] = useState('');
  const [contextExpanded, setContextExpanded] = useState(true);
  const [titles, setTitles] = useState<TitleSuggestionDto[]>([]);

  // Scope state
  const [scope, setScope] = useState<'book' | 'chapter' | 'paragraphs'>('book');
  const [scopeChapterId, setScopeChapterId] = useState('');

  // Chat state
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [input, setInput] = useState('');
  const [chatMode, setChatMode] = useState<ChatMode>('write');
  const [isGenerating, setIsGenerating] = useState(false);

  // Generation tracking
  const [jobId, setJobId] = useState<string | null>(null);
  const { event } = useGenerationStatus(jobId);
  const pendingMessageIdRef = useRef<string | null>(null);

  // Snackbar
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');

  // Ref for auto-scrolling chat
  const chatEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!session.data) return;
    setSynopsis(session.data.synopsis ?? '');
    setWorldNotes(session.data.worldBuildingNotes ?? '');
    setMood(session.data.mood ?? '');
    setPace(session.data.pace ?? '');
    setVoice(session.data.narrativeVoice ?? '');
  }, [session.data?.id]);

  // Auto-scroll chat on new messages
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Handle async generation results via SignalR
  useEffect(() => {
    if (!event || !pendingMessageIdRef.current) return;
    const done = event.status === 'complete';
    const failed = event.status === 'failed';

    if (done && event.payload) {
      const payload = event.payload as
        | { text?: string; generated_text?: string }
        | undefined;
      const resultText = payload?.generated_text ?? payload?.text ?? '';
      if (resultText) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === pendingMessageIdRef.current
              ? { ...m, content: resultText }
              : m,
          ),
        );
      }
      setIsGenerating(false);
      pendingMessageIdRef.current = null;
      setJobId(null);
    } else if (failed) {
      const errorPayload = event.payload as { error?: string } | undefined;
      setMessages((prev) =>
        prev.map((m) =>
          m.id === pendingMessageIdRef.current
            ? {
                ...m,
                content: `Generation failed: ${errorPayload?.error ?? 'Unknown error'}`,
              }
            : m,
        ),
      );
      setIsGenerating(false);
      pendingMessageIdRef.current = null;
      setJobId(null);
    }
  }, [event]);

  const buildSystemPrompt = useCallback((): string => {
    const parts: string[] = [];
    parts.push(
      'You are a creative writing AI assistant helping the author draft prose for a book.',
    );
    if (synopsis) parts.push(`Synopsis: ${synopsis}`);
    if (mood) parts.push(`Mood: ${mood}`);
    if (pace) parts.push(`Pace: ${pace}`);
    if (voice) parts.push(`Narrative voice: ${voice}`);
    if (worldNotes) parts.push(`World-building notes: ${worldNotes}`);
    if (scope === 'chapter' && scopeChapterId) {
      const chapter = chapters.data?.find((c) => c.id === scopeChapterId);
      if (chapter) {
        parts.push(`Scope: Chapter "${chapter.title}"`);
      } else {
        parts.push(`Scope: Chapter ${scopeChapterId}`);
      }
    } else if (scope === 'paragraphs') {
      parts.push('Scope: Selected paragraphs in the Book Editor');
    } else {
      parts.push('Scope: Entire book');
    }
    if (draft.trim()) parts.push(`Current working draft:\n${draft}`);
    return parts.join('\n\n');
  }, [
    synopsis,
    mood,
    pace,
    voice,
    worldNotes,
    draft,
    scope,
    scopeChapterId,
    chapters.data,
  ]);

  const buildPromptWithMode = useCallback(
    (userInstruction: string, mode: ChatMode): string => {
      switch (mode) {
        case 'expand':
          return `Expand on the current draft with more detail and description. Additional instruction: ${userInstruction}`;
        case 'rewrite':
          return `Rewrite the current draft in the following style: ${userInstruction}`;
        case 'continue':
          return `Continue writing from where the draft ends. Additional instruction: ${userInstruction}`;
        case 'summarize':
          return `Create a concise synopsis of the current draft. Additional instruction: ${userInstruction}`;
        case 'write':
        default:
          return userInstruction;
      }
    },
    [],
  );

  const handleSend = useCallback(async () => {
    const trimmed = input.trim();
    if (!trimmed || isGenerating) return;

    // Add user message
    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: trimmed,
      mode: chatMode,
      timestamp: new Date(),
    };

    // Add placeholder AI message
    const aiMsgId = crypto.randomUUID();
    const aiMsg: ChatMessage = {
      id: aiMsgId,
      role: 'assistant',
      content: '',
      mode: chatMode,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMsg, aiMsg]);
    setInput('');
    setIsGenerating(true);
    pendingMessageIdRef.current = aiMsgId;

    try {
      const prompt = buildPromptWithMode(trimmed, chatMode);
      const result = await textAction.mutateAsync({
        prompt,
        system_prompt: buildSystemPrompt(),
        max_tokens: 1000,
        temperature: 0.7,
      });

      setJobId(result.jobId);

      // Handle synchronous results
      if (result.result) {
        const payload = result.result as
          | { text?: string; generated_text?: string }
          | undefined;
        const resultText = payload?.generated_text ?? payload?.text ?? '';
        if (resultText) {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === aiMsgId ? { ...m, content: resultText } : m,
            ),
          );
          setIsGenerating(false);
          pendingMessageIdRef.current = null;
          setJobId(null);
        }
      }
    } catch (err) {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === aiMsgId
            ? { ...m, content: `Error: ${(err as Error).message}` }
            : m,
        ),
      );
      setIsGenerating(false);
      pendingMessageIdRef.current = null;
      setJobId(null);
    }
  }, [
    input,
    chatMode,
    isGenerating,
    textAction,
    buildSystemPrompt,
    buildPromptWithMode,
  ]);

  const handleAccept = useCallback(
    (messageId: string) => {
      setMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, accepted: true } : m)),
      );
      const msg = messages.find((m) => m.id === messageId);
      if (msg) {
        setDraft((prev) => (prev ? `${prev}\n\n${msg.content}` : msg.content));
      }
    },
    [messages],
  );

  const handleReject = useCallback((messageId: string) => {
    setMessages((prev) => prev.filter((m) => m.id !== messageId));
  }, []);

  const handleStartEdit = useCallback((messageId: string) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === messageId ? { ...m, editing: true } : m)),
    );
  }, []);

  const handleEditChange = useCallback(
    (messageId: string, newContent: string) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId ? { ...m, content: newContent } : m,
        ),
      );
    },
    [],
  );

  const handleAcceptEdited = useCallback(
    (messageId: string) => {
      const msg = messages.find((m) => m.id === messageId);
      if (msg) {
        setDraft((prev) => (prev ? `${prev}\n\n${msg.content}` : msg.content));
      }
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId ? { ...m, editing: false, accepted: true } : m,
        ),
      );
    },
    [messages],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const wordCount = draft.trim() ? draft.trim().split(/\s+/).length : 0;

  if (session.isLoading) return <CircularProgress />;

  return (
    <Box
      sx={{
        height: 'calc(100vh - 100px)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <BookEngineNav bookId={bookId} />
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h4" sx={{ flex: 1 }}>
          Story Studio
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            size="small"
            disabled={upsert.isPending}
            onClick={() =>
              upsert.mutateAsync({
                synopsis,
                worldBuildingNotes: worldNotes,
                mood,
                pace,
                narrativeVoice: voice,
              })
            }
          >
            Save session
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={suggestTitles.isPending}
            onClick={async () => {
              const results = await suggestTitles.mutateAsync();
              setTitles(results);
            }}
          >
            {suggestTitles.isPending ? 'Thinking...' : 'Suggest titles'}
          </Button>
        </Stack>
      </Stack>

      {/* Title suggestions */}
      <Collapse in={titles.length > 0}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Title suggestions
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {titles.map((t, i) => (
              <Paper
                key={i}
                variant="outlined"
                sx={{ p: 1, flex: '1 1 200px', maxWidth: 300 }}
              >
                <Typography variant="body2" fontWeight={600}>
                  {t.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t.rationale}
                </Typography>
              </Paper>
            ))}
          </Stack>
        </Paper>
      </Collapse>

      {/* Two-panel layout */}
      <Stack direction="row" spacing={2} sx={{ flex: 1, minHeight: 0 }}>
        {/* Left panel: Chat */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
          }}
        >
          {/* Collapsible session context */}
          <Accordion
            expanded={contextExpanded}
            onChange={(_, expanded) => setContextExpanded(expanded)}
            disableGutters
            elevation={0}
            sx={{ border: 1, borderColor: 'divider', mb: 1, flexShrink: 0 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">Session Context</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1.5}>
                <TextField
                  label="Synopsis"
                  value={synopsis}
                  onChange={(e) => setSynopsis(e.target.value)}
                  multiline
                  minRows={2}
                  size="small"
                />
                <TextField
                  label="World-building notes"
                  value={worldNotes}
                  onChange={(e) => setWorldNotes(e.target.value)}
                  multiline
                  minRows={2}
                  size="small"
                />
                <Stack direction="row" spacing={1}>
                  <FilterableSelect
                    label="Mood"
                    value={mood}
                    onChange={(newValue) => setMood(newValue)}
                    allowEmpty={true}
                    emptyLabel="(none)"
                    options={[
                      { value: 'hopeful', label: 'Hopeful' },
                      { value: 'somber', label: 'Somber' },
                      { value: 'tense', label: 'Tense' },
                      { value: 'playful', label: 'Playful' },
                      { value: 'mystical', label: 'Mystical' },
                      { value: 'grim', label: 'Grim' },
                    ]}
                    sx={{ flex: 1 }}
                  />
                  <FilterableSelect
                    label="Pace"
                    value={pace}
                    onChange={(newValue) => setPace(newValue)}
                    allowEmpty={true}
                    emptyLabel="(none)"
                    options={[
                      { value: 'slow', label: 'Slow' },
                      { value: 'measured', label: 'Measured' },
                      { value: 'brisk', label: 'Brisk' },
                      { value: 'breakneck', label: 'Breakneck' },
                    ]}
                    sx={{ flex: 1 }}
                  />
                  <FilterableSelect
                    label="Narrative voice"
                    value={voice}
                    onChange={(newValue) => setVoice(newValue)}
                    allowEmpty={true}
                    emptyLabel="(none)"
                    options={[
                      { value: 'detached', label: 'Detached' },
                      { value: 'intimate', label: 'Intimate' },
                      { value: 'lyrical', label: 'Lyrical' },
                      { value: 'journalistic', label: 'Journalistic' },
                      { value: 'wry', label: 'Wry' },
                    ]}
                    sx={{ flex: 1 }}
                  />
                </Stack>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Scope
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Chip
                    label="Entire Book"
                    variant={scope === 'book' ? 'filled' : 'outlined'}
                    color={scope === 'book' ? 'primary' : 'default'}
                    onClick={() => {
                      setScope('book');
                      setScopeChapterId('');
                    }}
                    clickable
                  />
                  <Chip
                    label="Current Chapter"
                    variant={scope === 'chapter' ? 'filled' : 'outlined'}
                    color={scope === 'chapter' ? 'primary' : 'default'}
                    onClick={() => setScope('chapter')}
                    clickable
                  />
                  <Chip
                    label="Selected Paragraphs"
                    variant={scope === 'paragraphs' ? 'filled' : 'outlined'}
                    color={scope === 'paragraphs' ? 'primary' : 'default'}
                    onClick={() => {
                      setScope('paragraphs');
                      setScopeChapterId('');
                    }}
                    clickable
                  />
                </Stack>
                {scope === 'chapter' && (
                  <FilterableSelect
                    label="Chapter"
                    value={scopeChapterId}
                    onChange={(newValue) => setScopeChapterId(newValue)}
                    allowEmpty={true}
                    emptyLabel="(select a chapter)"
                    options={(chapters.data ?? []).map((c) => ({
                      value: c.id,
                      label: c.title,
                    }))}
                    sx={{ mt: 1 }}
                  />
                )}
                {scope === 'paragraphs' && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    Paragraphs are selected in the Book Editor. The AI will
                    focus on those selected paragraphs.
                  </Typography>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* Chat thread */}
          <Paper
            variant="outlined"
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            {/* Messages area */}
            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
              }}
            >
              {messages.length === 0 && (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Start a conversation with the AI to draft your story. Choose
                    a mode and type your instruction below.
                  </Typography>
                </Box>
              )}

              {messages.map((msg) => (
                <ChatBubble
                  key={msg.id}
                  message={msg}
                  isGenerating={
                    isGenerating &&
                    msg.content === '' &&
                    msg.role === 'assistant'
                  }
                  onAccept={() => handleAccept(msg.id)}
                  onReject={() => handleReject(msg.id)}
                  onStartEdit={() => handleStartEdit(msg.id)}
                  onEditChange={(val) => handleEditChange(msg.id, val)}
                  onAcceptEdited={() => handleAcceptEdited(msg.id)}
                />
              ))}

              <div ref={chatEndRef} />
            </Box>

            {/* Input bar */}
            <Box
              sx={{
                borderTop: 1,
                borderColor: 'divider',
                p: 1.5,
                display: 'flex',
                gap: 1,
                alignItems: 'flex-end',
              }}
            >
              <FilterableSelect
                value={chatMode}
                onChange={(newValue) =>
                  setChatMode((newValue || 'write') as ChatMode)
                }
                allowEmpty={false}
                options={CHAT_MODES.map((m) => ({
                  value: m.value,
                  label: m.label,
                }))}
                sx={{ minWidth: 130 }}
              />
              <TextField
                placeholder={`${CHAT_MODES.find((m) => m.value === chatMode)?.description ?? 'Type your instruction'}...`}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                multiline
                maxRows={4}
                size="small"
                sx={{ flex: 1 }}
              />
              <IconButton
                color="primary"
                onClick={handleSend}
                disabled={!input.trim() || isGenerating}
              >
                <SendIcon />
              </IconButton>
            </Box>
          </Paper>
        </Box>

        {/* Right panel: Working Draft */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1 }}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              Working Draft
            </Typography>
            <Button
              variant="outlined"
              size="small"
              disabled={!draft.trim()}
              onClick={() => {
                if (!draft.trim()) return;
                navigator.clipboard.writeText(draft);
                setSnackMessage(
                  'Draft copied to clipboard — paste into the Book Editor',
                );
                setSnackOpen(true);
                if (bookId) {
                  navigate({
                    to: '/books/$bookId',
                    params: { bookId },
                  });
                }
              }}
            >
              Send to Book Editor
            </Button>
          </Stack>
          <TextField
            multiline
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Accepted prose from the chat will appear here. You can also type directly."
            sx={{
              flex: 1,
              '& .MuiInputBase-root': {
                height: '100%',
                alignItems: 'flex-start',
              },
              '& .MuiInputBase-input': {
                height: '100% !important',
                overflow: 'auto !important',
              },
            }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 0.5, textAlign: 'right' }}
          >
            {wordCount.toLocaleString()} word{wordCount !== 1 ? 's' : ''}
          </Typography>
        </Box>
      </Stack>

      <Snackbar
        open={snackOpen}
        autoHideDuration={4000}
        onClose={() => setSnackOpen(false)}
        message={snackMessage}
      />
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/*  ChatBubble sub-component                                           */
/* ------------------------------------------------------------------ */

function ChatBubble({
  message,
  isGenerating,
  onAccept,
  onReject,
  onStartEdit,
  onEditChange,
  onAcceptEdited,
}: {
  message: ChatMessage;
  isGenerating: boolean;
  onAccept: () => void;
  onReject: () => void;
  onStartEdit: () => void;
  onEditChange: (val: string) => void;
  onAcceptEdited: () => void;
}) {
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
      }}
    >
      <Box
        sx={{
          maxWidth: '85%',
          minWidth: 80,
        }}
      >
        {/* Mode chip */}
        {isUser && (
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ display: 'block', textAlign: 'right', mb: 0.25 }}
          >
            {message.mode}
          </Typography>
        )}

        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            bgcolor: isUser ? 'primary.main' : 'grey.100',
            color: isUser ? 'primary.contrastText' : 'text.primary',
            borderRadius: 2,
            borderTopRightRadius: isUser ? 0 : undefined,
            borderTopLeftRadius: isAssistant ? 0 : undefined,
          }}
        >
          {isAssistant && isGenerating ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                AI is thinking...
              </Typography>
            </Stack>
          ) : message.editing ? (
            <TextField
              multiline
              fullWidth
              value={message.content}
              onChange={(e) => onEditChange(e.target.value)}
              size="small"
              variant="standard"
              sx={{
                '& .MuiInputBase-input': { fontSize: '0.875rem' },
              }}
            />
          ) : (
            <Typography
              variant="body2"
              sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
            >
              {message.content}
            </Typography>
          )}
        </Paper>

        {/* Action buttons for assistant messages */}
        {isAssistant &&
          !isGenerating &&
          message.content &&
          !message.accepted && (
            <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
              {message.editing ? (
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  startIcon={<CheckIcon />}
                  onClick={onAcceptEdited}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  Accept edited
                </Button>
              ) : (
                <>
                  <IconButton
                    size="small"
                    color="success"
                    onClick={onAccept}
                    title="Accept"
                  >
                    <CheckIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={onReject}
                    title="Reject"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={onStartEdit} title="Edit">
                    <EditIcon fontSize="small" />
                  </IconButton>
                </>
              )}
            </Stack>
          )}

        {/* Accepted badge */}
        {isAssistant && message.accepted && (
          <Typography
            variant="caption"
            color="success.main"
            sx={{ mt: 0.25, display: 'block' }}
          >
            Accepted
          </Typography>
        )}
      </Box>
    </Box>
  );
}
